import Button from 'components/Form/Button';
import BasicTable from 'components/Form/Table';
import ModalSolFinanciamentos from 'components/Modal/ModalSolfinanciamentos';
import TableWrapper from 'components/TableWrapper';
import ContainerAuthenticated from 'containers/Authenticated';
import { CoreContext } from 'context/CoreContext';
import { useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ReadCuradoriaSolFinanciamentos as Read } from 'services/financing-proposals';
import { StatusContent } from 'ui/styled';
import { capitalize, exposeStrapiError, selectColors } from 'utils';
import { formatElapsedTime, parseCurrency } from 'utils/parsers';
import { formatadores, normalizeName } from 'utils/validators';

// Normaliza campo: remove '-', '', null, undefined, espaços
const normalizeField = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value).trim();
  return str === '' || str === '-' ? '' : str;
};

export default function SolFinanciamentos() {
  const { user, filterExpression } = useContext(CoreContext);
  const [loading, setLoading] = useState(false);
  const [registers, setRegisters] = useState([]);
  const [tick, setTick] = useState(0);
  const [modalData, setModalData] = useState(null);

  const init = async () => {
    setLoading(true);
    try {
      const result = await Read();
      if (result && !exposeStrapiError(result)) {
        const sorted = result?.results?.sort((a, b) => b.id - a.id);
        setRegisters(sorted);
      } else {
        toast.error('Erro ao carregar dados.');
      }
    } catch (e) {
      console.error(e);
      toast.error('Falha ao carregar propostas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => setTick((prev) => prev + 1), 120000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => init(), [tick]);

  // ---------------------------------------------------------------------------
  // COLUNAS
  // ---------------------------------------------------------------------------
  const columns = useMemo(() => {
    const baseColumns = [
      { title: 'Id', sortable: true, ref: 'id' },
      {
        title: 'Data',
        sortable: true,
        ref: 'date',
        renderCell: ({ row }) => formatadores.TIMESTAMP_TO_BR(row?.date),
      },
      {
        title: 'SLA',
        renderCell: ({ row }) => {
          const diffDays = formatadores.DIFF_DAYS_NOW(row?.date);
          return (
            <span style={{ color: diffDays >= 2 ? 'red' : 'inherit' }}>
              {formatElapsedTime(row?.date)}
            </span>
          );
        },
      },
    ];
    const distributorColumn = {
      title: 'Distribuidora',
      sortable: true,
      ref: 'distributor',
    };
    const integratorClientColumns = [
      { title: 'Integrador', sortable: true, ref: 'integrator' },
      { title: 'Cliente', sortable: true, ref: 'customer' },
    ];
    const valueColumn = { title: 'Valor (R$)', ref: 'value' };
    const newColumns = [
      { title: 'Banco', sortable: true, ref: 'banco' },
      { title: 'Status', renderCell: ({ row }) => capitalize(row?.status) },
      // TAG 100% GARANTIDA: AZUL SÓ COM AMBOS
      {
        title: 'TAG',
        ref: 'motivo_agente',
        renderCell: ({ row }) => {
          const motivo = normalizeField(row?.curadoria_ocorrido);
          const agente = normalizeField(row?.agente);
          const hasMotivo = !!motivo;
          const hasAgente = !!agente;
          // CHAVES QUE EXISTEM NO selectColors
          let tagKey = 'warning'; // nenhum
          if (hasMotivo && hasAgente) {
            tagKey = 'blue'; // AMBOS → AZUL
          } else if (hasMotivo || hasAgente) {
            tagKey = 'warning'; // APENAS UM → LARANJA
          }
          const text =
            [hasMotivo ? motivo : null, hasAgente ? agente : null]
              .filter(Boolean)
              .join(' / ') || '—';
          return (
            <StatusContent color={selectColors(tagKey)}>{text}</StatusContent>
          );
        },
      },
      {
        title: 'Telefone',
        renderCell: ({ row }) => {
          if (!row?.telefone || row?.telefone === '-') return '-';
          const numeroLimpo = row.telefone.replace(/\D/g, '');
          const linkWhats = `https://wa.me/55${numeroLimpo}`;
          return (
            <a
              href={linkWhats}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'blue',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              {row.telefone}
            </a>
          );
        },
      },
      {
        title: 'Reagendar',
        renderCell: ({ row }) => {
          const qtdContato = row.qtd_contato ?? 0;
          const podeReagendar = qtdContato <= 3;
          if (!podeReagendar) {
            return (
              <span style={{ color: '#000000ff', fontSize: '1.0em' }}>
                Foi feito ({qtdContato} contatos)
              </span>
            );
          }
          return (
            <Button
              color="blue"
              nospace
              small
              mbutton
              onClick={() => setModalData(row.original)}
            >
              Reagendar
            </Button>
          );
        },
      },
    ];
    let finalColumns = [...baseColumns];
    if (
      user?.administrador ||
      (user?.distribuidor && user?.distribuidores?.length > 0)
    ) {
      finalColumns.push(distributorColumn);
    }
    finalColumns.push(...integratorClientColumns);
    finalColumns.push(valueColumn);
    finalColumns.push(...newColumns);
    return finalColumns;
  }, [user]);

  // ---------------------------------------------------------------------------
  // LINHAS: Sem forçar '-'
  // ---------------------------------------------------------------------------
  const rows = useMemo(() => {
    return registers?.map((item) => {
      let monitoring = item?.monitoring ?? null;
      if (Array.isArray(monitoring)) monitoring = monitoring[0];
      if (monitoring?.data?.attributes) monitoring = monitoring.data.attributes;
      return {
        id: item?.id,
        date: item?.createdAt,
        formatedDate: formatadores.TIMESTAMP_TO_BR(item?.createdAt),
        distributor: normalizeName(item?.distribuidor?.trade_name),
        integrator: normalizeName(item?.integrador?.nome_fantasia),
        customer: normalizeName(item?.informacoes_cliente?.nome_completo),
        value: parseCurrency(item?.informacoes_projeto?.custo_total_projeto),
        curadoria_ocorrido: item?.curadoria_ocorrido,
        banco:
          normalizeName(
            item?.propostas_banco?.find(
              (pb) => pb?.status?.toLowerCase() === 'aprovado'
            )?.banco?.nome_fantasia
          ) ?? '-',
        telefone: monitoring?.telefone ?? '-',
        agente: monitoring?.agente,
        status: item?.status,
        qtd_contato: item?.estatisticas_etapas.total_contatos ?? 0,
        original: item,
        // cnpj_integrador: item?.integrador?.cnpj_integrador,
        // cnpj_integrador_clean: item?.integrador?.cnpj_integrador,.replace(
        //   /\D/g,
        //   ''
        // ),
        // cpf_cnpj: item?.informacoes_cliente?.cpf || item?.informacoes_cliente?.cnpj,
        // cpf_cnpj_clean: a?.proposta_financeira?.cpf
        //   ? item?.informacoes_cliente?.cpf.replace(/\D/g, '')
        //   : item?.informacoes_cliente?.cnpj.replace(/\D/g, ''),
      };
    });
  }, [registers]);

  return (
    <ContainerAuthenticated>
      <TableWrapper title="SOL | Financiamentos" search>
        <BasicTable
          loading={loading}
          columns={columns}
          rows={rows?.filter(filterExpression)}
          pagination
        />
      </TableWrapper>
      {modalData && (
        <ModalSolFinanciamentos
          proposalData={modalData}
          reload={init}
          onClose={() => setModalData(null)}
        />
      )}
    </ContainerAuthenticated>
  );
}
